import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { snakeCase, sortBy, uniqBy } from 'lodash';

import { Configuration, FilterType, GetPreTradePortfolioRequest, PortfolioApi } from '../_openapi/portfolio-optimizer';
import { processFilter } from '../widgets/filters/filterUtils';
import { defaultApiConfig } from './defaultApiConfig';

const portfolioApi = new PortfolioApi(new Configuration(defaultApiConfig));

export const usePortfolios = ({
    dataVariant = 'report',
    pagination,
    sorting,
    columnFilters,
    enabled = true,
}: {
    dataVariant?: DataVariant;
    pagination?: PaginationState;
    sorting?: SortingState;
    columnFilters?: ColumnFiltersState;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: ['portfolio', 'list', { pagination, sorting, columnFilters, dataVariant }],
        queryFn: () => {
            const query: GetPreTradePortfolioRequest = {};

            if (pagination) {
                query.pageIndex = pagination.pageIndex;
                query.pageSize = pagination.pageSize;
            }

            if (sorting?.length) {
                query.sortLabel = snakeCase(sorting[0].id);
                query.sortDesc = sorting[0].desc;
            }

            if (columnFilters?.length) {
                query.filter = columnFilters.map(processFilter);
            }

            switch (dataVariant) {
                case 'report':
                    return portfolioApi.getReportPortfolio(query);
                case 'preTrade':
                    return portfolioApi.getPreTradePortfolio(query);
                default:
                    throw new Error('Unknown data variant');
            }
        },
        placeholderData: keepPreviousData,
        enabled,
    });

export const usePortfolio = ({ pId, dataVariant = 'report' }: { pId?: string; dataVariant?: DataVariant }) =>
    useQuery({
        queryKey: ['portfolio', 'details', pId, dataVariant],
        queryFn: async () => {
            const query = {
                pageIndex: 0,
                pageSize: 1,
                filter: [
                    {
                        type: FilterType.STRING,
                        field: 'p_id',
                        value: pId,
                    },
                ],
            };

            const page = await (dataVariant === 'report'
                ? portfolioApi.getReportPortfolio(query)
                : portfolioApi.getPreTradePortfolio(query));
            return page.portfolios[0];
        },
        enabled: !!pId,
    });

export const usePortfolioLookup = (portfolioIdentifier?: string) =>
    useQuery({
        queryKey: ['portfolio', 'lookup', portfolioIdentifier],
        queryFn: async ({ signal }) => {
            return lookupPortfolio(portfolioIdentifier!, signal);
        },
        enabled: !!portfolioIdentifier,
    });

export const lookupPortfolio = async (portfolioIdentifier: string, signal?: AbortSignal) => {
    const response = await portfolioApi.getReportPortfolio(
        {
            filter: [
                {
                    type: 'list',
                    field: '',
                    value: [
                        {
                            type: FilterType.STRING,
                            field: 'portfolio_guid',
                            value: portfolioIdentifier,
                        },
                        {
                            type: FilterType.STRING,
                            field: 'account_alias',
                            value: portfolioIdentifier,
                        },
                        {
                            type: FilterType.STRING,
                            field: 'a_c',
                            value: portfolioIdentifier,
                        },
                        {
                            type: FilterType.STRING,
                            field: 'p_id',
                            value: portfolioIdentifier,
                        },
                    ],
                },
            ],
        },
        { signal },
    );

    return sortBy(uniqBy(response.portfolios, 'base.pId'), 'base.portfolioGuid');
};

export const useHistoricalStatistics = () =>
    useQuery({
        queryKey: ['statistics', 'history'],
        queryFn: () => {
            return portfolioApi.getHistoricalStatistics();
        },
    });

export const useBrokerAccountRestrictions = (pIds: string[]) =>
    useQuery({
        queryKey: ['portfolio', 'brokerAccountRestrictions', [...pIds].sort()],
        queryFn: () => portfolioApi.getBrokerAccountRestrictions({ requestBody: pIds }),
        enabled: pIds.length > 0,
        placeholderData: keepPreviousData,
    });
